// Public signing page — opened by clients via magic-link SMS.
// No auth required. The token is the URL parameter.

import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, AlertCircle, Loader2, FileText } from 'lucide-react';
import { SignaturePad } from '@/components/ui/SignaturePad';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { ThemeToggle } from '@/components/ThemeToggle';
import { BlurText, MagicRings, ShinyText } from '@/components/anim';
import { apiBaseUrl } from '@/lib/api';
import type { KaufanbotData } from '@/pdf/Kaufanbot';
import { formatDateTime } from '@/lib/formatLocale';

export const Route = createFileRoute('/sign/$token')({
  component: SignPage,
});

interface DocumentInfo {
  document: {
    id: string;
    type: 'vermittlungsauftrag' | 'besichtigungsschein' | 'kaufanbot';
    number: number;
    contract_variant: 'allein' | 'einfach' | null;
    signed: number;
    signatures_required: number;
    signatures_collected: number;
    created_at: number;
  };
  client_label: string | null;
  property_label: string | null;
  /** For multi-signer Kaufanbot — the buyer this magic-link is addressed to. */
  signer_label: string | null;
  expires_at: number;
}

const TYPE_TITLE: Record<DocumentInfo['document']['type'], string> = {
  vermittlungsauftrag: 'Allein-Vermittlungsauftrag',
  besichtigungsschein: 'Besichtigungsschein',
  kaufanbot: 'Befristetes Kaufanbot',
};

/** The name the signer sees on the consent screen. Two contracts share the
 *  `vermittlungsauftrag` type since migration 0058, and telling someone they
 *  are signing an exclusive agreement when they are not is the one thing this
 *  page must never do. Anything other than an explicit 'einfach' keeps the
 *  historical title, since every pre-0058 document is exclusive. */
function documentTitle(doc: DocumentInfo['document']): string {
  if (doc.type === 'vermittlungsauftrag' && doc.contract_variant === 'einfach') {
    return 'Vermittlungsauftrag';
  }
  return TYPE_TITLE[doc.type];
}

function SignPage() {
  const { t } = useTranslation();
  const { token } = Route.useParams();
  const [info, setInfo] = useState<DocumentInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${apiBaseUrl}/sign/${token}`);
        if (!res.ok) {
          const j = (await res.json().catch(() => ({}))) as { message?: string };
          throw new Error(j.message ?? `failed_${res.status}`);
        }
        const data = (await res.json()) as DocumentInfo;
        if (!cancelled) setInfo(data);
      } catch (cause) {
        if (!cancelled) setError((cause as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const onCapture = async (base64: string) => {
    setSubmitting(true);
    try {
      const res = await fetch(`${apiBaseUrl}/sign/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageBase64: base64 }),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(j.message ?? `failed_${res.status}`);
      }
      const j = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        fully_signed?: boolean;
        needs_finalize?: boolean;
      };
      // Batch AI-1 — public sign page only does browser-side finalize for
      // Kaufanbot (multi-buyer Käufer signatures need to be embedded
      // side-by-side in the binding PDF). Vermittlungsauftrag and
      // Besichtigungsschein get their signed-PDF lock established lazily
      // the first time an operator downloads from the CRM — see
      // AppointmentDocuments.onDownloadSigned.
      if (j.needs_finalize && info?.document.type === 'kaufanbot') {
        await finalizeMultiSigPdf(token);
      }
      setDone(true);
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="relative min-h-screen overflow-hidden bg-paper px-4 py-8">
      <MagicRings
        size={760}
        rings={5}
        opacity={0.1}
        style={{ top: '-25%', left: '50%', transform: 'translateX(-50%)' }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(60% 40% at 50% 0%, hsl(var(--color-primary) / 0.05) 0%, transparent 70%)',
        }}
      />
      <div className="absolute end-4 top-4 z-10 flex items-center gap-2">
        <ThemeToggle />
        <LanguageSwitcher />
      </div>

      <div className="relative z-10 mx-auto max-w-xl">
        {/* Brand */}
        <header className="mb-6 flex items-center justify-center gap-3">
          <span
            className="flex h-12 w-12 items-center justify-center rounded-xl font-display text-2xl italic"
            style={{
              background: 'linear-gradient(135deg, hsl(var(--brand-gold)), hsl(var(--brand-gold-deep)))',
              color: '#1a1305',
            }}
          >
            4
          </span>
          <div className="leading-tight">
            <BlurText as="h1" className="block font-display text-xl text-ink">
              4 M Immobilien
            </BlurText>
            <p className="text-[11px] uppercase text-ink-3 tracking-[0.12em]">
              <ShinyText speed={6}>{t('sign.brand_sub', 'Dokumentenunterschrift')}</ShinyText>
            </p>
          </div>
        </header>

        <div className="rounded-2xl border border-line bg-card p-6 shadow-strong">
          {error ? (
            <div className="text-center">
              <AlertCircle className="mx-auto mb-3 h-12 w-12 text-[hsl(var(--danger))]" />
              <h2 className="font-display text-xl text-ink">
                {t('sign.error.title', 'Link nicht gültig')}
              </h2>
              <p className="mt-2 text-sm text-ink-3">{messageFor(error, t)}</p>
              <p className="mt-4 text-xs text-ink-3">
                {t(
                  'sign.error.contact',
                  'Bitte kontaktieren Sie Ihren Makler für einen neuen Link.',
                )}
              </p>
            </div>
          ) : done ? (
            <div className="text-center">
              <CheckCircle2 className="mx-auto mb-3 h-14 w-14 text-[hsl(var(--positive))]" />
              <h2 className="font-display text-2xl text-ink">
                {t('sign.done.title', 'Vielen Dank!')}
              </h2>
              <p className="mt-2 text-sm text-ink-2">
                {t(
                  'sign.done.body',
                  'Ihre Unterschrift wurde sicher gespeichert. Sie können dieses Fenster jetzt schließen.',
                )}
              </p>
            </div>
          ) : !info ? (
            <div className="flex items-center justify-center py-12 text-ink-3">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : info.document.signed ? (
            <div className="text-center">
              <CheckCircle2 className="mx-auto mb-3 h-12 w-12 text-[hsl(var(--positive))]" />
              <h2 className="font-display text-xl text-ink">
                {t('sign.already.title', 'Bereits unterschrieben')}
              </h2>
              <p className="mt-2 text-sm text-ink-3">
                {t(
                  'sign.already.body',
                  'Dieses Dokument wurde bereits unterschrieben.',
                )}
              </p>
            </div>
          ) : (
            <>
              <div className="mb-5">
                <p className="text-xs uppercase text-ink-3 tracking-[0.12em]">
                  {t('sign.intro', 'Sie unterschreiben')}
                </p>
                <h2 className="mt-1 flex items-start gap-2 font-display text-xl text-ink">
                  <FileText className="mt-1 h-4 w-4 text-ink-3" />
                  <span>
                    {documentTitle(info.document)}
                    <span className="ms-2 font-mono text-base text-ink-3">
                      Nr. {info.document.number}
                    </span>
                  </span>
                </h2>
                {info.client_label && (
                  <p className="mt-2 text-sm text-ink-2">
                    {t('sign.party', 'Vertragspartei:')}{' '}
                    <span className="font-medium">{info.client_label}</span>
                  </p>
                )}
                {info.property_label && (
                  <p className="text-xs text-ink-3">{info.property_label}</p>
                )}

                {info.signer_label && info.document.signatures_required > 1 && (
                  <div className="mt-3 rounded-lg border border-[hsl(var(--brand-gold))]/40 bg-[hsl(var(--brand-gold))]/5 p-3">
                    <p className="text-xs uppercase text-ink-3 tracking-[0.12em]">
                      {t('sign.multi.signer_label', 'Sie unterzeichnen als')}
                    </p>
                    <p className="mt-1 text-sm font-semibold text-ink">
                      {info.signer_label}
                    </p>
                    <p className="mt-1 text-xs text-ink-2">
                      {t(
                        'sign.multi.progress',
                        `Bisher ${info.document.signatures_collected} von ${info.document.signatures_required} Käufern unterzeichnet.`,
                        {
                          collected: info.document.signatures_collected,
                          required: info.document.signatures_required,
                        },
                      )}
                    </p>
                  </div>
                )}

                <p className="mt-3 text-[11px] text-ink-3">
                  {t('sign.expires_at', 'Link gültig bis')}{' '}
                  {formatDateTime(new Date(info.expires_at))}
                </p>
              </div>

              <SignaturePad
                onCapture={onCapture}
                disabled={submitting}
                height={200}
              />

              <p className="mt-3 text-[11px] text-ink-3">
                {t(
                  'sign.legal',
                  'Mit dem Bestätigen bestätigen Sie, dieses Dokument elektronisch unterschrieben zu haben. Datum, Uhrzeit und Geräte-Information werden zu Beweiszwecken gespeichert.',
                )}
              </p>
            </>
          )}
        </div>

        <p className="mt-6 text-center text-[11px] uppercase text-ink-3 tracking-[0.12em]">
          4 M Immobilien CRM · Wien
        </p>
      </div>
    </main>
  );
}

function messageFor(err: string, t: (k: string, fb: string) => string): string {
  if (err === 'token_not_found') return t('sign.error.not_found', 'Link wurde nicht gefunden.');
  if (err === 'token_expired') return t('sign.error.expired', 'Dieser Link ist abgelaufen.');
  if (err === 'token_already_used')
    return t('sign.error.used', 'Dieser Link wurde bereits verwendet.');
  return err;
}

interface RenderData {
  document_id: string;
  type: 'kaufanbot';
  number: number;
  offer: KaufanbotData['offer'];
  property: KaufanbotData['property'];
  lawyer: KaufanbotData['lawyer'];
  agent_name: string;
  buyers: KaufanbotData['buyers'];
  sellers: KaufanbotData['sellers'];
  buyer_signatures: Array<{
    client_id: string;
    label: string;
    data_url: string;
    signed_at: number;
  }>;
}

async function finalizeMultiSigPdf(token: string): Promise<void> {
  const dataRes = await fetch(`${apiBaseUrl}/sign/${token}/render-data`);
  if (!dataRes.ok) {
    const j = (await dataRes.json().catch(() => ({}))) as { message?: string };
    throw new Error(j.message ?? `render_data_failed_${dataRes.status}`);
  }
  const data = (await dataRes.json()) as RenderData;

  // Dynamic-import the PDF generator so the public sign page bundle stays
  // small for single-signer flows (which never need this code path).
  const [{ generateKaufanbotBlob }] = await Promise.all([
    import('@/pdf/offerPdfGenerator'),
  ]);

  const pdfData: KaufanbotData = {
    offer: data.offer,
    property: data.property,
    lawyer: data.lawyer ?? null,
    agentName: data.agent_name,
    buyers: data.buyers,
    sellers: data.sellers,
    language: 'de',
    buyerSignatures: data.buyer_signatures.map((s) => ({
      label: s.label,
      data_url: s.data_url,
      signed_at: s.signed_at,
    })),
  };

  const blob = await generateKaufanbotBlob(pdfData);
  const uploadRes = await fetch(`${apiBaseUrl}/sign/${token}/finalize-pdf`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/pdf' },
    body: blob,
  });
  if (!uploadRes.ok) {
    const j = (await uploadRes.json().catch(() => ({}))) as { message?: string };
    throw new Error(j.message ?? `finalize_failed_${uploadRes.status}`);
  }
}
